// window.NostoSearch — thin wrapper around nostojs for SERP, category and
// tracking. Used by sections/search.liquid (Phase 3) and collection
// templates (Phase 4). Autocomplete is handled separately by
// assets/nosto-autocomplete.js using @nosto/autocomplete.
(function () {
  'use strict';

  // Nosto's SearchProduct schema does NOT expose `handle` — we derive it
  // from `url` via getHandleFromUrl().
  const DEFAULTS = {
    productFields: [
      'productId', 'name', 'url', 'imageUrl',
      'price', 'listPrice', 'priceCurrencyCode',
      'brand', 'available', 'categories',
      'customFields.key', 'customFields.value'
    ],
    keywordFields: ['keyword', '_highlight.keyword'],
    serp:     { pageSize: 36 },
    category: { pageSize: 36 }
  };

  const api = () => {
    return new Promise((resolve) => {
      if (typeof window.nostojs === 'function') return window.nostojs(resolve);
      let attempts = 0;
      const t = setInterval(() => {
        if (typeof window.nostojs === 'function') {
          clearInterval(t); window.nostojs(resolve);
        } else if (++attempts > 100) {
          clearInterval(t);
          console.warn('[NostoSearch] nostojs not available after 10s.');
        }
      }, 100);
    });
  };

  const serpSearch = (query, opts) => {
    opts = opts || {};
    return api().then((nostoApi) => {
      return nostoApi.search({
        query: query,
        products: {
          facets: opts.facets || ['*'],
          fields: DEFAULTS.productFields,
          size:   opts.size || DEFAULTS.serp.pageSize,
          from:   opts.from || 0,
          sort:   opts.sort,
          filter: opts.filter
        }
      }, {
        track: 'serp',
        redirect: opts.redirect !== false,
        isKeyword: !!opts.isKeyword
      });
    });
  };

  // ---------------------------------------------------------------------
  // Direct, non-personalized search against Nosto's public search endpoint —
  // plain HTTP, no nostojs, no session, NO CONSENT DEPENDENCY (cookieless).
  // Verified to answer unauthenticated in ~200-300ms. This is what keeps
  // category grids, the SERP and autocomplete working when Nosto's bootstrap
  // never initializes (consent unanswered/declined, ad-blocked, or its
  // intermittent init stall). The full Nosto flow upgrades/replaces these
  // renders when — if — it boots.
  //
  // params: { query, categoryId, size, from, keywords: {size, highlight} }
  // Resolves { hits, total, keywords }. Rejects on HTTP errors (e.g. "search
  // is not enabled" for a market account that doesn't exist) so the account
  // walker below can distinguish a wrong account from an empty result.
  // ---------------------------------------------------------------------
  const directFetch = (account, params) => {
    const gql =
      'query($accountId: String, $query: String, $products: InputSearchProducts, $keywords: InputSearchKeywords) {' +
      ' search(accountId: $accountId, query: $query, products: $products, keywords: $keywords) {' +
      ' products { total hits { productId url } }' +
      ' keywords { total hits { keyword _redirect _highlight { keyword } } } } }';
    const products = { size: params.size || DEFAULTS.category.pageSize, from: params.from || 0 };
    if (params.categoryId) products.categoryId = String(params.categoryId);
    return fetch('https://search.nosto.com/v1/graphql', {
      method: 'POST',
      // text/plain keeps the request CORS-simple (no preflight) — same as
      // Nosto's own client.
      headers: { 'Content-Type': 'text/plain' },
      body: JSON.stringify({
        query: gql,
        variables: {
          accountId: account,
          query: params.query || '',
          products: products,
          keywords: params.keywords || { size: 0 }
        }
      })
    })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error('direct search HTTP ' + r.status))))
      .then((data) => {
        const s = data && data.data && data.data.search;
        return {
          hits: (s && s.products && s.products.hits) || [],
          total: (s && s.products && s.products.total) || 0,
          keywords: (s && s.keywords && s.keywords.hits) || []
        };
      });
  };

  // Nosto search accounts for this market, in preference order (suffixed
  // market account first, bare account as fallback — set in theme.liquid's
  // head). Once one answers, __nsDirectAccount pins it for the session.
  const directAccounts = () => {
    if (window.__nsDirectAccount) return [window.__nsDirectAccount];
    if (Array.isArray(window.__nsDirectAccounts)) return window.__nsDirectAccounts;
    const grid = document.querySelector('[data-nosto-grid]');
    return grid && grid.dataset.nostoMerchant ? [grid.dataset.nostoMerchant] : [];
  };

  // --- Nosto boot state ------------------------------------------------
  // Nosto's engine (personalization, merchandising, A/B, analytics) is the
  // PRIMARY for all search — the direct endpoint above must ONLY serve
  // sessions where Nosto cannot boot. These helpers let callers ask "is
  // Nosto up?" / "will it be up within N ms?" without each rolling their
  // own poll. nostojs callbacks only fire after the app has fully booted.
  let nostoBooted = false;
  const bootWaiters = [];
  (function watchBoot() {
    let attempts = 0;
    const t = setInterval(() => {
      if (typeof window.nostojs === 'function') {
        clearInterval(t);
        window.nostojs(() => {
          nostoBooted = true;
          bootWaiters.splice(0).forEach((fn) => { try { fn(); } catch (e) { /* ignore */ } });
        });
      } else if (++attempts > 240) {
        clearInterval(t); // stop watching after 60s
      }
    }, 250);
  })();

  const isNostoReady = () => nostoBooted;

  // Resolves true as soon as Nosto boots, false once timeoutMs passes first.
  const whenNostoReady = (timeoutMs) => new Promise((resolve) => {
    if (nostoBooted) return resolve(true);
    const timer = setTimeout(() => resolve(false), timeoutMs);
    bootWaiters.push(() => { clearTimeout(timer); resolve(true); });
  });

  // Cookie-consent state via Cookiebot (this store's CMP — Nosto's loader is
  // gated on the consent it feeds into Shopify's Customer Privacy API).
  // true  = banner unanswered or declined -> Nosto will not boot
  // false = consent granted               -> Nosto is expected to boot
  // null  = unknown (Cookiebot not loaded yet / blocked)
  const consentDenied = () => {
    try {
      const cb = window.Cookiebot;
      if (cb && typeof cb.hasResponse === 'boolean') {
        if (!cb.hasResponse) return true;
        return !(cb.consent && (cb.consent.marketing || cb.consent.statistics));
      }
    } catch (e) { /* ignore */ }
    return null;
  };

  // Direct search that walks the account list: an HTTP error (wrong account,
  // e.g. "search is not enabled") falls through to the next; an empty result
  // from a valid account is a real answer and is returned as-is.
  const directSearchAny = (params) => {
    const accounts = directAccounts();
    const tryAt = (i) => {
      if (i >= accounts.length) return Promise.reject(new Error('no direct search account answered'));
      return directFetch(accounts[i], params).then(
        (res) => { window.__nsDirectAccount = accounts[i]; return res; },
        () => tryAt(i + 1)
      );
    };
    return tryAt(0);
  };

  const categorySearch = (args, opts) => {
    opts = opts || {};
    return api().then((nostoApi) => {
      return nostoApi.search({
        products: {
          categoryId:   args.categoryId,
          categoryPath: args.categoryPath,
          facets: opts.facets || ['*'],
          fields: DEFAULTS.productFields,
          size:   opts.size || DEFAULTS.category.pageSize,
          from:   opts.from || 0,
          sort:   opts.sort,
          filter: opts.filter
        }
      }, {
        track: 'category',
        redirect: opts.redirect !== false
      });
    });
  };

  const recordSubmit = (query, options) => {
    return api().then((a) => {
      if (options) a.recordSearchSubmit(query, options);
      else a.recordSearchSubmit(query);
    });
  };
  const recordClick = (type, hit) => {
    return api().then((a) => { a.recordSearchClick(type, hit); });
  };
  const recordAddToCart = (type, hit) => {
    return api().then((a) => { a.recordSearchAddToCart(type, hit); });
  };

  const getHandleFromUrl = (url) => {
    if (!url) return '';
    try {
      const path = url.indexOf('://') > -1 ? new URL(url).pathname : url.split('?')[0];
      const m = path.match(/\/products\/([^/?#]+)/);
      return m ? m[1] : '';
    } catch (e) { return ''; }
  };

  // Shared card cache + fetch for Section Rendering API
  const cardCache = {};

  // A card's HTML as rendered, before the theme's scripts (Flickity etc.) change it.
  // cacheCard puts it back after a page load, e.g. from the category grid's Back snapshot.
  const cardHtml = (handle) => cardCache[handle] || '';
  const cacheCard = (handle, html) => { if (handle && html) cardCache[handle] = html; };

  // Cap concurrent Section Rendering API calls: a 36-product page otherwise fires
  // 36 fetches at once, starving the card images and other requests behind them.
  // Jobs run in queue order, so the top of the grid fills first.
  const CARD_CONCURRENCY = 8;
  let activeCardFetches = 0;
  const cardFetchQueue = [];

  const pumpCardQueue = () => {
    while (activeCardFetches < CARD_CONCURRENCY && cardFetchQueue.length) {
      const job = cardFetchQueue.shift();
      activeCardFetches++;
      job.fn().then(
        (v) => { activeCardFetches--; pumpCardQueue(); job.resolve(v); },
        (e) => { activeCardFetches--; pumpCardQueue(); job.reject(e); }
      );
    }
  };

  const scheduleCardFetch = (fn) => new Promise((resolve, reject) => {
    cardFetchQueue.push({ fn: fn, resolve: resolve, reject: reject });
    pumpCardQueue();
  });

  const fetchCard = (handle) => {
    if (cardCache[handle]) return Promise.resolve(cardCache[handle]);
    // Prefix the current market/locale root (e.g. /is-is/) so the Section Rendering API
    // renders the card in the active market — otherwise product.url, currency and
    // translations come back in the default (US) market and links go to the wrong locale.
    const root = (window.theme && window.theme.routes && window.theme.routes.root) || '/';
    return scheduleCardFetch(() => fetch(root + 'products/' + handle + '?section_id=nosto-product-card')
      .then((r) => r.ok ? r.text() : '')
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const el = doc.querySelector('[data-nosto-card-section]');
        // A nonexistent/unpublished product still renders 200 with an empty
        // ghost card (blank link, sold-out styling) — require a real product
        // link so those cells get removed instead of showing an empty card.
        const valid = el && el.querySelector('a[href*="/products/"]');
        const out = valid ? el.innerHTML.trim() : '';
        if (out) cardCache[handle] = out;
        return out;
      })
      .catch(() => ''));
  };

  // Batch card rendering: ONE Section Rendering API request for a whole page of
  // hits, via Shopify storefront search `id:` queries rendered by
  // sections/nosto-product-cards-batch.liquid (36 products used to mean 36
  // separate /products/<handle>?section_id=nosto-product-card requests).
  // Resolves to a map of handle -> card HTML; missing products (unsearchable,
  // just unpublished, etc.) simply aren't in the map and fall back to the
  // per-product fetchCard path.
  const BATCH_CHUNK = 40; // storefront search pages at 50; stay under it
  const fetchCardsBatchChunk = (ids) => {
    const root = (window.theme && window.theme.routes && window.theme.routes.root) || '/';
    const q = ids.map((id) => 'id:' + id).join(' OR ');
    const url = root + 'search?q=' + encodeURIComponent(q) + '&type=product&section_id=nosto-product-cards-batch';
    // The collection template preflights the first page's batch HTML inline
    // (see theme.liquid) — consume it instead of re-fetching. The preflight
    // may cover MORE ids than this chunk (it over-fetches so the grid can be
    // sliced to its exact page size); any superset works, because cards are
    // matched back by handle.
    let htmlPromise = null;
    const pre = window.__nsBatchPreflight;
    if (pre) {
      const preIds = pre.q.split(' OR ');
      if (ids.every((id) => preIds.indexOf('id:' + id) !== -1)) {
        htmlPromise = pre.promise;
        window.__nsBatchPreflight = null;
      }
    }
    return (htmlPromise || fetch(url).then((r) => r.ok ? r.text() : ''))
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const map = {};
        doc.querySelectorAll('[data-batch-card]').forEach((el) => {
          const handle = el.getAttribute('data-handle');
          const out = el.innerHTML.trim();
          if (handle && out) {
            map[handle] = out;
            cardCache[handle] = out;
          }
        });
        return map;
      })
      .catch(() => ({}));
  };

  // Mirrors snippets/nosto-skeleton.liquid so pending cells match the initial skeleton grid.
  const SKELETON_HTML =
    '<div class="ns-serp-skeleton">' +
      '<div class="ns-serp-skeleton__image"></div>' +
      '<div class="ns-serp-skeleton__info">' +
        '<div class="ns-serp-skeleton__title"></div>' +
        '<div class="ns-serp-skeleton__subtitle"></div>' +
        '<div class="ns-serp-skeleton__bottom">' +
          '<div class="ns-serp-skeleton__price"></div>' +
        '</div>' +
      '</div>' +
    '</div>';

  // Progressive grid cells for SERP/category. Returns { cells, done }: `cells` are
  // skeleton placeholders in hit order, ready to insert immediately; each one swaps
  // in its card HTML as its Section Rendering API response lands, instead of gating
  // the whole grid on the slowest of 36+ requests. A cell whose card fails to render
  // removes itself. `trackType` is 'serp' or 'category' for click/ATC tracking.
  const buildCards = (hits, trackType) => {
    const cells = [];
    const cellHits = []; // parallel to cells (hits without a handle create no cell)
    const fills = [];
    hits.forEach((hit) => {
      const handle = getHandleFromUrl(hit.url);
      if (!handle) return;
      const div = document.createElement('div');
      div.className = 'grid-item product-item ns-card-pending';
      div.dataset.handle = handle;
      // fetchCard strips the [data-nosto-card-section] wrapper, so re-tag the cell —
      // the delegated submit handler below relies on it for AJAX add-to-cart.
      div.setAttribute('data-nosto-card-section', '');
      div.innerHTML = SKELETON_HTML;
      div.addEventListener('click', (e) => {
        // Quick Add: track before the theme's cart logic fires
        const addBtn = e.target.closest('[data-add-to-cart]');
        if (addBtn) {
          recordAddToCart(trackType, { productId: hit.productId, url: hit.url });
          return;
        }
        // Product click: navigation to PDP
        const anchor = e.target.closest('a[href*="/products/"]');
        if (anchor && !e.defaultPrevented) {
          recordClick(trackType, { productId: hit.productId, url: hit.url });
        }
      });
      cells.push(div);
      cellHits.push(hit);
    });

    // Fill cells: one batch request per BATCH_CHUNK hits; anything the batch
    // could not resolve falls back to the per-product section render. Cached
    // cards fill synchronously without joining a batch.
    const fillCell = (div, html) => {
      if (html) {
        div.innerHTML = html;
        div.classList.remove('ns-card-pending');
      } else {
        div.remove();
      }
    };

    const pending = []; // { div, handle, id }
    cells.forEach((div, i) => {
      const handle = div.dataset.handle;
      if (cardCache[handle]) {
        fillCell(div, cardCache[handle]);
      } else {
        const hit = cellHits[i];
        pending.push({ div: div, handle: handle, id: hit && hit.productId });
      }
    });

    for (let i = 0; i < pending.length; i += BATCH_CHUNK) {
      const chunk = pending.slice(i, i + BATCH_CHUNK);
      const withIds = chunk.filter((c) => c.id);
      const withoutIds = chunk.filter((c) => !c.id);
      withoutIds.forEach((c) => fills.push(fetchCard(c.handle).then((html) => fillCell(c.div, html))));
      if (withIds.length) {
        fills.push(
          fetchCardsBatchChunk(withIds.map((c) => c.id)).then((map) => {
            const misses = [];
            withIds.forEach((c) => {
              if (map[c.handle]) fillCell(c.div, map[c.handle]);
              else misses.push(c);
            });
            return Promise.all(misses.map((c) => fetchCard(c.handle).then((html) => fillCell(c.div, html))));
          })
        );
      }
    }

    return { cells: cells, done: Promise.all(fills) };
  };

  window.NostoSearch = {
    defaults: DEFAULTS,
    api: api,
    serpSearch: serpSearch,
    categorySearch: categorySearch,
    directSearchAny: directSearchAny,
    isNostoReady: isNostoReady,
    whenNostoReady: whenNostoReady,
    consentDenied: consentDenied,
    recordSubmit: recordSubmit,
    recordClick: recordClick,
    recordAddToCart: recordAddToCart,
    getHandleFromUrl: getHandleFromUrl,
    fetchCard: fetchCard,
    cardHtml: cardHtml,
    cacheCard: cacheCard,
    buildCards: buildCards
  };

  // --- Nosto rec card ATC fix ---
  // When <nosto-dynamic-card> inserts section HTML via innerHTML, the
  // theme's quick-add-product custom element may not attach its AJAX
  // handler (it checks for cart-drawer at connectedCallback time and
  // skips if not found). This causes the form to submit natively to
  // /cart/add, redirecting the page. Fix: intercept form submits from
  // Nosto card wrappers and dispatch the theme's cart:add event instead.
  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form.closest('[data-nosto-card-section]') && !form.closest('[data-nosto-cart-rec-section]')) return;
    if (form.getAttribute('action') && form.getAttribute('action').indexOf('/cart/add') === -1) return;

    var btn = form.querySelector('[data-add-to-cart]');
    if (!btn || btn.hasAttribute('disabled')) return;

    e.preventDefault();
    e.stopPropagation();

    document.dispatchEvent(new CustomEvent('theme:cart:add', {
      detail: { button: btn },
      bubbles: true
    }));
  }, true);

  // --- Nosto cart rec card: size select → variant ID wiring ---
  // Inline scripts don't execute when injected via innerHTML by nosto-dynamic-card.
  // Use a delegated change listener instead.
  document.addEventListener('change', function (e) {
    var select = e.target;
    if (!select.matches('[data-nosto-size-select]')) return;
    var form = select.closest('form');
    if (!form) return;
    var hiddenInput = form.querySelector('[data-nosto-variant-id]');
    if (hiddenInput) {
      hiddenInput.value = select.value;
    }
  });

  // --- Recommendation row placeholders ---
  // nosto.css reserves the height of empty homepage placements so the row
  // doesn't push the page down when Nosto injects it. A placement Nosto
  // leaves empty (no campaign for this visitor) must not keep that gap, so
  // release the reservation once Nosto has rendered. The fallback covers
  // Nosto never booting (blocked, offline); it waits for window load so a
  // slow connection can't collapse the space just before the row arrives.
  if (document.querySelector('.template-index .main-content .shopify-app-block > .nosto_element:empty')) {
    const releaseRecoPlaceholders = () => document.documentElement.classList.add('ns-reco-released');
    let recoFallback = null;
    const armRecoFallback = () => { recoFallback = setTimeout(releaseRecoPlaceholders, 8000); };
    if (document.readyState === 'complete') armRecoFallback();
    else window.addEventListener('load', armRecoFallback, { once: true });

    api().then((nostoApi) => {
      nostoApi.listen('postrender', () => {
        clearTimeout(recoFallback);
        window.removeEventListener('load', armRecoFallback);
        releaseRecoPlaceholders();
      });
    });
  }

  // --- Nosto rec campaign ATC attribution ---
  // When a user adds to cart from a Nosto recommendation campaign card,
  // fire recordAttribution so Nosto credits the campaign for the conversion.
  // Uses delegated listeners to handle async-rendered cards without
  // MutationObserver complexity. Scoped to .nosto_element containers.
  //
  // NOTE: Search/category ATC attribution uses recordSearchAddToCart (in
  // nosto-serp.js / nosto-category.js) — that is separate and already
  // confirmed correct by Nosto support. Do not mix the two.
  document.addEventListener('theme:cart:add', function (e) {
    var btn = e.detail && e.detail.button;
    if (!btn) return;

    // Only track ATCs originating from Nosto recommendation elements
    var nostoEl = btn.closest('.nosto_element');
    if (!nostoEl) return;

    // Get product ID from the <nosto-dynamic-card> wrapper
    var card = btn.closest('nosto-dynamic-card');
    var productId = card && card.getAttribute('data-product-id');

    // Fallback: try form's product-id input or data attribute on wrapper
    if (!productId) {
      var form = btn.closest('form');
      var pidInput = form && form.querySelector('[name="product-id"]');
      productId = pidInput ? pidInput.value : null;
    }

    if (!productId) return;

    try {
      nostojs(function (api) {
        api.recordAttribution({
          type: 'cp',
          target: productId,
          ref: nostoEl.getAttribute('id')   // Explicitly pass the Slot ID
        }).done();
      });
    } catch (err) {
      console.warn('[Nosto] recordAttribution failed', err);
    }
  });
})();
